/**
 * LangGraph Client
 *
 * HTTP/SSE client for communicating with LangGraph Cloud API
 * Uses native browser APIs (fetch, ReadableStream) for zero dependencies
 */

import { appConfig } from '@/config/app-config';
import { STORAGE_KEYS } from '@/config/storage-config';
import type {
  LangGraphThread,
  LangGraphThreadState,
  LangGraphRunInput,
  LangGraphStreamEvent,
  LangGraphStreamEventType,
  StreamCallbacks,
  StreamController,
  LangGraphError,
  LangGraphPartialMessageEvent,
  LangGraphCompleteMessageEvent,
  LangGraphInterruptEvent,
  IntermediateStep,
  PendingInterrupt,
} from '@/types/langgraph';

/**
 * LangGraph Client Class
 * Handles all HTTP and SSE communication with LangGraph Cloud
 */
export class LangGraphClient {
  private assistantId: string;

  constructor(assistantId?: string) {
    this.assistantId = assistantId ?? 'agent';
  }

  /**
   * Get the current API key from localStorage
   * Dynamically retrieves the token to ensure we always have the latest value
   */
  private getApiKey(): string {
    return localStorage.getItem(STORAGE_KEYS.TOKEN) || '';
  }

  /**
   * Create a new thread in LangGraph
   */
  async createThread(metadata?: Record<string, any>): Promise<LangGraphThread> {
    const url = `${appConfig.API_BASE_URL}/agents/${this.assistantId}/threads`;

    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        metadata: metadata || {},
        agent_id: this.assistantId,
      }),
    });

    if (!response.ok) {
      throw await this.handleErrorResponse(response);
    }

    return response.json();
  }

  /**
   * Get thread state including message history
   */
  async getThreadState(threadId: string): Promise<LangGraphThreadState> {
    const url = `${appConfig.API_BASE_URL}/agents/${this.assistantId}/threads/${threadId}/state`;

    const response = await this.fetchWithRetry(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw await this.handleErrorResponse(response);
    }

    return response.json();
  }

  /**
   * Search/list threads for this assistant
   */
  async searchThreads(params: { limit: number; sortBy?: string; sortOrder?: string }): Promise<any[]> {
    const url = `${appConfig.API_BASE_URL}/agents/${this.assistantId}/threads/search`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        limit: params.limit,
        sort_by: params.sortBy ?? 'updated_at',
        sort_order: params.sortOrder ?? 'desc',
      }),
    });
    if (!response.ok) throw await this.handleErrorResponse(response);
    return response.json();
  }

  /**
   * Stream a run with Server-Sent Events
   * Returns a controller to manage the stream
   */
  async streamRun(
    threadId: string,
    input: LangGraphRunInput,
    callbacks: StreamCallbacks,
    signal?: AbortSignal
  ): Promise<StreamController> {
    const url = `${appConfig.API_BASE_URL}/agents/${this.assistantId}/threads/${threadId}/runs/stream`;

    let status: 'active' | 'paused' | 'aborted' = 'active';
    let streamTimeout: NodeJS.Timeout | null = null;
    let lastActivityTime = Date.now();

    // State object to track message accumulation
    const state = {
      currentMessageId: '',
      accumulatedContent: '',
    };

    const controller: StreamController = {
      abort: () => {
        status = 'aborted';
        if (streamTimeout) {
          clearTimeout(streamTimeout);
        }
      },
      pause: () => {
        status = 'paused';
      },
      resume: () => {
        status = 'active';
      },
      get status() {
        return status;
      },
    };

    // Setup stream timeout mechanism (300 seconds of inactivity)
    const resetTimeout = () => {
      if (streamTimeout) {
        clearTimeout(streamTimeout);
      }
      lastActivityTime = Date.now();
      streamTimeout = setTimeout(() => {
        const timeSinceLastActivity = Date.now() - lastActivityTime;
        if (timeSinceLastActivity >= 300000 && status === 'active') {
          console.warn('⏰ [STREAM-TIMEOUT] Stream timed out after 300 seconds of inactivity');
          console.warn('⏰ [STREAM-TIMEOUT] Last activity:', new Date(lastActivityTime).toISOString());
          console.warn('⏰ [STREAM-TIMEOUT] Current accumulated content length:', state.accumulatedContent?.length || 0);

          // Complete the message if we have accumulated content
          if (state.accumulatedContent && callbacks.onComplete) {
            callbacks.onComplete({
              id: state.currentMessageId,
              type: 'ai',
              content: state.accumulatedContent,
            });
          }
          // End the stream
          if (callbacks.onEnd) {
            callbacks.onEnd();
          }
          status = 'aborted';
        }
      }, 300000);
    };

    // Handle abort signal
    if (signal) {
      signal.addEventListener('abort', () => {
        controller.abort();
      });
    }

    try {
      // Create request body
      const requestBody = {
        input: input.input,
        config: {
          ...input.config,
          configurable: {
            ...input.config?.configurable,
          },
        },
        stream_mode: [input.streamMode || 'messages'],
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(requestBody),
        signal,
      });

      if (!response.ok) {
        throw await this.handleErrorResponse(response);
      }

      // Process SSE stream manually
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      // Start the timeout
      resetTimeout();

      // Process stream
      this.processStream(
        reader,
        decoder,
        callbacks,
        controller,
        state,
        () => status,
        resetTimeout
      );
    } catch (error) {
      if (streamTimeout) {
        clearTimeout(streamTimeout);
      }
      if (callbacks.onError && error instanceof Error) {
        callbacks.onError({
          message: error.message,
          code: 'STREAM_ERROR',
        }, false);  // hasMessageId = false (error before streaming)
      }
      throw error;
    }

    return controller;
  }

  /**
   * Rewind the last conversation turn — removes the last human message and all
   * subsequent AI/tool messages from the LangGraph checkpoint.
   */
  async rewindLastTurn(threadId: string): Promise<{ success: boolean; removed_count: number; remaining_count: number }> {
    const url = `${appConfig.API_BASE_URL}/agents/${this.assistantId}/threads/${threadId}/rewind`;

    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw await this.handleErrorResponse(response);
    }

    return response.json();
  }

  /**
   * Soft-delete a thread
   */
  async deleteThread(threadId: string): Promise<void> {
    const url = `${appConfig.API_BASE_URL}/agents/${this.assistantId}/threads/${threadId}`;
    const response = await this.fetchWithRetry(url, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!response.ok) {
      throw await this.handleErrorResponse(response);
    }
  }

  /**
   * Resume a run after an interrupt
   */
  async resumeRun(threadId: string, runId: string, input: any): Promise<void> {
    const url = `${appConfig.API_BASE_URL}/agents/${this.assistantId}/threads/${threadId}/runs/${runId}/resume`;

    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ input }),
    });

    if (!response.ok) {
      throw await this.handleErrorResponse(response);
    }
  }

  /**
   * Process SSE stream manually
   */
  private async processStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    decoder: TextDecoder,
    callbacks: StreamCallbacks,
    controller: StreamController,
    state: { currentMessageId: string; accumulatedContent: string },
    getStatus: () => 'active' | 'paused' | 'aborted',
    resetTimeout: () => void
  ): Promise<void> {
    let buffer = '';
    let currentEvent: string | null = null;

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done || getStatus() === 'aborted') {
          // Ensure completion callbacks are called when stream ends
          if (state.accumulatedContent && callbacks.onComplete) {
            callbacks.onComplete({
              id: state.currentMessageId,
              type: 'ai',
              content: state.accumulatedContent,
            });
          }
          if (callbacks.onEnd) {
            callbacks.onEnd();
          }
          break;
        }

        // Skip if paused
        if (getStatus() === 'paused') {
          continue;
        }

        // Reset timeout on activity
        resetTimeout();

        buffer += decoder.decode(value, { stream: true });

        // Split by double newline to get complete SSE events
        const events = buffer.split('\n\n');
        // Keep last incomplete event in buffer
        buffer = events.pop() || '';

        // Process each complete SSE event
        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          const lines = eventBlock.split('\n');
          let eventType: string | null = null;
          let eventData: string | null = null;
          let eventId: string | null = null;

          for (const line of lines) {
            if (line.startsWith('id: ')) {
              eventId = line.slice(4).trim();
            } else if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }

          // Skip if no data
          if (!eventData) continue;

          if (eventData === '[DONE]') {
            if (callbacks.onEnd) {
              callbacks.onEnd();
            }
            continue;
          }

          try {
            const parsed = JSON.parse(eventData);

            // If we have an event type, wrap the data
            if (eventType) {
              const event: LangGraphStreamEvent = {
                event: eventType as LangGraphStreamEventType,
                data: parsed,
              };
              this.handleStreamEvent(event, callbacks, state, resetTimeout);
              continue;
            }

              // Handle standard SSE event format (has 'event' property in JSON)
              if (parsed && typeof parsed === 'object' && 'event' in parsed && !Array.isArray(parsed)) {
                const event: LangGraphStreamEvent = parsed;
                this.handleStreamEvent(event, callbacks, state, resetTimeout);
                continue;
              }

              // Handle array of messages format (direct LangGraph response without event wrapper)
              if (Array.isArray(parsed)) {
                // Check if this is a completion event (has stopReason in response_metadata)
                let isComplete = false;

                // Process each message in the array
                parsed.forEach((msg) => {
                  // Handle both 'ai' and 'AIMessageChunk' types
                  const isAIMessage = msg.type === 'ai' || msg.type === 'AIMessageChunk';

                  if (isAIMessage && msg.content !== undefined && msg.content !== null) {
                    // Extract text content from LangChain message format
                    let textContent = '';

                    if (Array.isArray(msg.content)) {
                      // Content is array of content blocks
                      textContent = msg.content
                        .filter((block: any) => {
                          // Only include text blocks or blocks with text/content properties
                          if (!block) return false;
                          if (typeof block === 'string') return true;
                          if (block.type === 'text') return true;
                          // Include blocks without a type only if they have text or content
                          if (block.type === undefined && (block.text || block.content)) return true;
                          return false;
                        })
                        .map((block: any) => {
                          // Handle various content structures
                          if (typeof block === 'string') {
                            return block;
                          }
                          // Return text or content, or empty string (never String(block))
                          return block.text || block.content || '';
                        })
                        .filter((text: string) => text !== 'undefined' && text !== 'null' && text !== '')
                        .join('');
                    } else if (typeof msg.content === 'string') {
                      textContent = msg.content;
                    } else {
                      // Fallback for unexpected content types
                      textContent = String(msg.content);
                    }

                    if (textContent) {
                      // Determine if this is a new message or continuation
                      const isNewMessage = state.currentMessageId !== msg.id;

                      // Reset accumulation for new messages
                      if (isNewMessage) {
                        state.currentMessageId = msg.id;
                        state.accumulatedContent = '';
                      }

                      // Append incremental chunk to accumulated content
                      state.accumulatedContent += textContent;

                      // Send full accumulated content to UI
                      if (callbacks.onToken) {
                        callbacks.onToken(msg.id, state.accumulatedContent);
                      }

                      // Reset timeout on token receive
                      resetTimeout();

                      // Check if message is complete (has stopReason)
                      if (msg.response_metadata?.stopReason) {
                        isComplete = true;

                        // Send completion callback
                        if (callbacks.onComplete) {
                          callbacks.onComplete({
                            id: msg.id,
                            type: 'ai',
                            content: textContent,
                          });
                        }
                      }
                    }
                  }
                });

                // Signal end of stream if message is complete
                if (isComplete && callbacks.onEnd) {
                  callbacks.onEnd();
                }

                continue;
              }
          } catch (error) {
            console.error('❌ [SSE-PARSE] Failed to parse event data:', error);
          }
        }
      }
    } catch (error) {
      if (callbacks.onError && error instanceof Error) {
        callbacks.onError({
          message: error.message,
          code: 'STREAM_PROCESSING_ERROR',
        }, false);  // hasMessageId = false (error before streaming)
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Handle individual stream events
   */
  private handleStreamEvent(
    event: LangGraphStreamEvent,
    callbacks: StreamCallbacks,
    state: { currentMessageId: string; accumulatedContent: string },
    resetTimeout: () => void
  ): void {
    // console.log(`🔄 [HANDLE-EVENT] Received event type: "${event.event}"`);
    switch (event.event) {
      case 'metadata':
        if (callbacks.onMetadata && event.data) {
          callbacks.onMetadata({
            run_id: event.data.run_id,
            thread_id: event.data.thread_id,
          });
        }
        break;

      case 'messages': {
        // Handle the standard "messages" event type from LangGraph
        if (callbacks.onToken && Array.isArray(event.data)) {
          event.data.forEach((msg: any) => {
            // Tool messages (LangChain ToolMessage) carry the result of a
            // tool invocation. We forward them as `tool_result` intermediate
            // steps so consumers (BackupSection, executor progress, etc.)
            // can pick up the payload — without this, downstream listeners
            // wait forever and "Save / Download" actions never fire.
            if (msg.type === 'tool' && callbacks.onIntermediateStep) {
              const rawContent = typeof msg.content === 'string'
                ? msg.content
                : Array.isArray(msg.content)
                  ? msg.content.map((b: any) =>
                      typeof b === 'string' ? b : (b?.text ?? '')
                    ).join('')
                  : '';
              let parsedOutput: any = undefined;
              if (rawContent) {
                try {
                  parsedOutput = JSON.parse(rawContent);
                } catch {
                  /* leave undefined; consumers will fall back to content */
                }
              }
              callbacks.onIntermediateStep({
                id: `tool-result-${msg.tool_call_id ?? msg.id ?? Date.now()}`,
                type: 'tool_result',
                timestamp: Date.now(),
                content: rawContent,
                toolName: msg.name,
                toolOutput: parsedOutput,
                metadata: { tool_call_id: msg.tool_call_id },
              });
              return;
            }

            // Handle both 'ai' and 'AIMessageChunk' types
            const isAIMessage = msg.type === 'ai' || msg.type === 'AIMessageChunk';

            if (isAIMessage && msg.content !== undefined && msg.content !== null) {
              // Extract text content from LangChain message format
              let textContent = '';

              if (Array.isArray(msg.content)) {
                // Content is array of content blocks
                textContent = msg.content
                  .filter((block: any) => {
                    // Only include text blocks or blocks with text/content properties
                    if (!block) return false;
                    if (typeof block === 'string') return true;
                    if (block.type === 'text') return true;
                    // Include blocks without a type only if they have text or content
                    if (block.type === undefined && (block.text || block.content)) return true;
                    return false;
                  })
                  .map((block: any) => {
                    // Handle various content structures
                    if (typeof block === 'string') {
                      return block;
                    }
                    // Return text or content, or empty string (never String(block))
                    return block.text || block.content || '';
                  })
                  .filter((text: string) => text !== 'undefined' && text !== 'null' && text !== '')
                  .join('');
              } else if (typeof msg.content === 'string') {
                textContent = msg.content;
              } else {
                // Fallback for unexpected content types
                textContent = String(msg.content);
              }

              const messageId = msg.id;

              if (textContent) {
                // Track message ID and send full accumulated content
                const isNewMessage = messageId !== state.currentMessageId;

                // Reset accumulation for new messages
                if (isNewMessage) {
                  state.currentMessageId = messageId;
                  state.accumulatedContent = '';
                }

                // Append incremental chunk to accumulated content
                state.accumulatedContent += textContent;

                // Send full accumulated content to UI
                callbacks.onToken(messageId, state.accumulatedContent);
                resetTimeout();

                // Check for completion signal
                if (msg.response_metadata?.stopReason) {
                  if (callbacks.onComplete) {
                    callbacks.onComplete({
                      id: messageId,
                      type: 'ai',
                      content: textContent,
                    });
                  }
                }
              }

              // Handle tool calls as intermediate steps. Drop partial /
              // malformed entries (missing id or name) to avoid leaking
              // `tool-null` / `name: ''` steps that break the stream
              // provider's tool-message validator downstream.
              if (msg.tool_calls && callbacks.onIntermediateStep) {
                msg.tool_calls.forEach((toolCall: any) => {
                  if (typeof toolCall?.id !== 'string' || !toolCall.id) return;
                  if (typeof toolCall?.name !== 'string' || !toolCall.name) return;
                  const step: IntermediateStep = {
                    id: `tool-${toolCall.id}`,
                    type: 'tool_call',
                    timestamp: Date.now(),
                    content: `Calling ${toolCall.name}`,
                    toolName: toolCall.name,
                    toolInput: toolCall.args,
                  };
                  callbacks.onIntermediateStep!(step);
                });
              }
            }
          });
        }
        break;
      }

      case 'messages/partial': {
        const partialEvent = event as LangGraphPartialMessageEvent;
        if (callbacks.onToken && partialEvent.data) {
          // Handle array format (LangGraph Cloud sends array of messages)
          if (Array.isArray(partialEvent.data)) {
            partialEvent.data.forEach((msg: any) => {
              // Forward tool results — see comment in the `messages` case.
              if (msg.type === 'tool' && callbacks.onIntermediateStep) {
                const rawContent = typeof msg.content === 'string'
                  ? msg.content
                  : Array.isArray(msg.content)
                    ? msg.content.map((b: any) =>
                        typeof b === 'string' ? b : (b?.text ?? '')
                      ).join('')
                    : '';
                let parsedOutput: any = undefined;
                if (rawContent) {
                  try {
                    parsedOutput = JSON.parse(rawContent);
                  } catch {
                    /* leave undefined */
                  }
                }
                callbacks.onIntermediateStep({
                  id: `tool-result-${msg.tool_call_id ?? msg.id ?? Date.now()}`,
                  type: 'tool_result',
                  timestamp: Date.now(),
                  content: rawContent,
                  toolName: msg.name,
                  toolOutput: parsedOutput,
                  metadata: { tool_call_id: msg.tool_call_id },
                });
                return;
              }

              // Handle both 'ai' and 'AIMessageChunk' types
              const isAIMessage = msg.type === 'ai' || msg.type === 'AIMessageChunk';

              if (isAIMessage && msg.content !== undefined && msg.content !== null) {
                // Extract text content from LangChain message format
                let textContent = '';

                if (Array.isArray(msg.content)) {
                  // Content is array of content blocks
                  textContent = msg.content
                    .filter((block: any) => {
                      // Only include text blocks or blocks with text/content properties
                      if (!block) return false;
                      if (typeof block === 'string') return true;
                      if (block.type === 'text') return true;
                      // Include blocks without a type only if they have text or content
                      if (block.type === undefined && (block.text || block.content)) return true;
                      return false;
                    })
                    .map((block: any) => {
                      // Handle various content structures
                      if (typeof block === 'string') {
                        return block;
                      }
                      // Return text or content, or empty string (never String(block))
                      return block.text || block.content || '';
                    })
                    .filter((text: string) => text !== 'undefined' && text !== 'null' && text !== '')
                    .join('');
                } else if (typeof msg.content === 'string') {
                  textContent = msg.content;
                } else {
                  // Fallback for unexpected content types
                  textContent = String(msg.content);
                }

                const messageId = msg.id;

                // Track message ID and send full accumulated content
                const isNewMessage = messageId !== state.currentMessageId;

                // Reset accumulation for new messages
                if (isNewMessage) {
                  state.currentMessageId = messageId;
                  state.accumulatedContent = '';
                }

                // Append incremental chunk to accumulated content
                state.accumulatedContent += textContent;

                // Send full accumulated content to UI
                callbacks.onToken(messageId, state.accumulatedContent);
                resetTimeout();

                // Handle tool calls as intermediate steps
                if (msg.tool_calls && callbacks.onIntermediateStep) {
                  msg.tool_calls.forEach((toolCall: any) => {
                    const step: IntermediateStep = {
                      id: `tool-${toolCall.id}`,
                      type: 'tool_call',
                      timestamp: Date.now(),
                      content: `Calling ${toolCall.name}`,
                      toolName: toolCall.name,
                      toolInput: toolCall.args,
                    };
                    callbacks.onIntermediateStep!(step);
                  });
                }
              }
            });
          } else {
            // Handle object format (standard SSE format)
            const messageId = partialEvent.data.id;
            const content = partialEvent.data.content;

            // Skip empty content chunks
            if (content) {
              // Track message ID and send full accumulated content
              const isNewMessage = messageId !== state.currentMessageId;

              // Reset accumulation for new messages
              if (isNewMessage) {
                state.currentMessageId = messageId;
                state.accumulatedContent = '';
              }

              // Append incremental chunk to accumulated content
              state.accumulatedContent += content;

              // Send full accumulated content to UI
              callbacks.onToken(messageId, state.accumulatedContent);
              resetTimeout();
            }

            // Handle tool calls as intermediate steps (drop partials)
            if (partialEvent.data.tool_calls && callbacks.onIntermediateStep) {
              partialEvent.data.tool_calls.forEach((toolCall) => {
                if (typeof toolCall?.id !== 'string' || !toolCall.id) return;
                if (typeof toolCall?.name !== 'string' || !toolCall.name) return;
                const step: IntermediateStep = {
                  id: `tool-${toolCall.id}`,
                  type: 'tool_call',
                  timestamp: Date.now(),
                  content: `Calling ${toolCall.name}`,
                  toolName: toolCall.name,
                  toolInput: toolCall.args,
                };
                callbacks.onIntermediateStep!(step);
              });
            }
          }
        }
        break;
      }

      case 'messages/complete': {
        const completeEvent = event as LangGraphCompleteMessageEvent;
        if (callbacks.onComplete && completeEvent.data) {
          // Extract text content from message before passing to callback
          let textContent = '';

          if (Array.isArray(completeEvent.data.content)) {
            // Content is array of content blocks
            textContent = completeEvent.data.content
              .filter((block: any) => {
                // Only include text blocks or blocks with text/content properties
                if (!block) return false;
                if (typeof block === 'string') return true;
                if (block.type === 'text') return true;
                // Include blocks without a type only if they have text or content
                if (block.type === undefined && (block.text || block.content)) return true;
                return false;
              })
              .map((block: any) => {
                if (typeof block === 'string') return block;
                // Return text or content, or empty string (never String(block))
                return block.text || block.content || '';
              })
              .filter((text: string) => text !== 'undefined' && text !== 'null' && text !== '')
              .join('');
          } else if (typeof completeEvent.data.content === 'string') {
            textContent = completeEvent.data.content;
          } else if (completeEvent.data.content && typeof completeEvent.data.content === 'object') {
            // Try extracting from object properties
            const content = completeEvent.data.content as any;
            textContent = content.text || content.content || String(completeEvent.data.content);
          }

          callbacks.onComplete({
            id: completeEvent.data.id,
            type: completeEvent.data.type,
            content: textContent,
          });
        }
        break;
      }

      case 'interrupt': {
        const interruptEvent = event as LangGraphInterruptEvent;
        if (callbacks.onInterrupt && interruptEvent.data) {
          const interrupt: PendingInterrupt = {
            runId: interruptEvent.data.run_id,
            threadId: '', // Will be set by caller
            type: 'human_input',
            prompt: 'The agent needs your input to continue',
            metadata: interruptEvent.data.value,
          };
          callbacks.onInterrupt(interrupt);
        }
        break;
      }

      case 'error': {
        if (callbacks.onError && event.data) {
          callbacks.onError({
            message: event.data.message || 'Stream error',
            code: event.data.code || 'UNKNOWN_ERROR',
          }, !!state.currentMessageId);  // hasMessageId = true if we have received a message ID
        }
        break;
      }

      case 'custom':
        // Handle custom events (e.g., UI component events)
        console.log('🔍 [SSE-CUSTOM] Received custom event:', event);
        console.log('🔍 [SSE-CUSTOM] event.data:', JSON.stringify(event.data, null, 2));
        console.log('🔍 [SSE-CUSTOM] event.data.type:', event.data?.type);
        console.log('🔍 [SSE-CUSTOM] event.data.name:', event.data?.name);
        console.log('🔍 [SSE-CUSTOM] callbacks.onCustomEvent exists:', !!callbacks.onCustomEvent);

        if (callbacks.onCustomEvent && event.data) {
          console.log('✅ [SSE-CUSTOM] Calling onCustomEvent with data:', event.data);
          callbacks.onCustomEvent(event.data);
        } else {
          console.warn('⚠️ [SSE-CUSTOM] Not calling onCustomEvent - callback exists:', !!callbacks.onCustomEvent, 'data exists:', !!event.data);
        }
        break;

      case 'end':
        if (callbacks.onEnd) {
          callbacks.onEnd();
        }
        break;

      default:
        // Silently ignore unhandled event types
        break;
    }
  }

  /**
   * Get standard headers for API requests
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Dynamically get the latest token from localStorage
    const apiKey = this.getApiKey();

    // Only add API key if provided (supports public deployments)
    if (apiKey && apiKey.trim() !== '') {
      headers['Authorization'] = `Bearer ${apiKey}`;
    }

    return headers;
  }

  /**
   * Fetch with automatic retry and exponential backoff
   */
  private async fetchWithRetry(
    url: string,
    options: RequestInit,
    maxRetries = 3
  ): Promise<Response> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);

        // Don't retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          return response;
        }

        // Retry on server errors (5xx)
        if (response.status >= 500) {
          throw new Error(`Server error: ${response.status}`);
        }

        return response;
      } catch (error) {
        lastError = error as Error;

        // Don't retry on abort
        if (error instanceof Error && error.name === 'AbortError') {
          throw error;
        }

        // Exponential backoff: 1s, 2s, 4s
        if (attempt < maxRetries - 1) {
          await this.sleep(Math.pow(2, attempt) * 1000);
        }
      }
    }

    throw lastError || new Error('Max retries exceeded');
  }

  /**
   * Handle error responses from API
   */
  private async handleErrorResponse(response: Response): Promise<LangGraphError> {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    let details = null;

    try {
      const errorData = await response.json();
      message = errorData.message || errorData.error || message;
      details = errorData;
    } catch {
      // Response is not JSON, use status text
    }

    return {
      message,
      code: `HTTP_${response.status}`,
      status: response.status,
      details,
    };
  }

  /**
   * Utility function to sleep for a specified duration
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Per-assistantId client cache
 */
const clientCache = new Map<string, LangGraphClient>();

/**
 * Factory function — returns a cached LangGraphClient for the given assistant ID.
 */
export function getLangGraphClient(assistantId: string): LangGraphClient {
  if (!clientCache.has(assistantId)) {
    clientCache.set(assistantId, new LangGraphClient(assistantId));
  }
  return clientCache.get(assistantId)!;
}

/**
 * Default singleton instance — backward-compatible export for existing code.
 */
export const langGraphClient = getLangGraphClient('agent');
